import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Calendar, Clock, Home, Search, Sparkles, User } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import logger from "../../utils/logger";
import "./BlogList.css";

function timeAgo(dateString, isAr) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (isAr) {
      if (diffDays > 0) return `منذ ${diffDays} يوم`;
      return "اليوم";
    } else {
      if (diffDays > 0) return `${diffDays} days ago`;
      return "Today";
    }
  } catch {
    return dateString;
  }
}

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const TOPICS = [
    { id: "", labelAr: "جميع الموضوعات", labelEn: "All Topics" },
    { id: "Tech", labelAr: "الأخبار والحلول التقنية", labelEn: "Tech & News" },
    { id: "Freelance", labelAr: "دليل المستقلين والأعمال", labelEn: "Freelance & Business" },
    { id: "AI", labelAr: "الذكاء الاصطناعي", labelEn: "AI & Innovation" },
    { id: "Tutorials", labelAr: "الشروحات والدروس", labelEn: "Guides & Tutorials" },
  ];

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const data = await api.apiGet(`blog?lang=${encodeURIComponent(lang)}`);
        setPosts(data?.status === "success" && Array.isArray(data.posts) ? data.posts : []);
      } catch (e) {
        logger.error("Error loading blog posts", e);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [lang]);

  const filteredPosts = useMemo(() => {
    let result = posts;
    const q = search.trim().toLowerCase();

    if (q) {
      result = result.filter((post) => {
        const text = [post?.title, post?.excerpt, post?.category].filter(Boolean).join(" ").toLowerCase();
        return text.includes(q);
      });
    }

    if (selectedTopic) {
      result = result.filter((post) => String(post?.category || "").toLowerCase().includes(selectedTopic.toLowerCase()));
    }

    return result;
  }, [posts, search, selectedTopic]);

  return (
    <div className="blog-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="blog-container">
        {/* Breadcrumbs */}
        <nav className="d-flex align-items-center gap-2 mb-4 small text-muted">
          <Link to="/" className="text-decoration-none text-muted font-semibold d-flex align-items-center gap-1">
            <Home size={15} />
            <span>{isAr ? "الرئيسية" : "Home"}</span>
          </Link>
          <span>/</span>
          <strong className="text-success font-bold">{isAr ? "المدونة والمقالات" : "Blog & Knowledge Hub"}</strong>
        </nav>

        {/* Blog Hero Showcase */}
        <section className="blog-hero">
          <div className="blog-hero__eyebrow">
            <Sparkles size={16} />
            <span>{isAr ? "رؤى استراتيجية ومقالات تقنية" : "Strategic Insights & Tech Articles"}</span>
          </div>

          <h1>
            {isAr
              ? "اكتشف أحدث المقالات والتحليلات في التجارة والتقنية والأعمال"
              : "Explore Latest Deep Dives in Tech, Commerce & Business"}
          </h1>

          <p>
            {isAr
              ? "تسلط مدونة موسى جروب الضوء على أحدث التطورات، نصائح المستقلين، البرمجة، والذكاء الاصطناعي لمساعدتك في اتخاذ قرارات ملهمة."
              : "Discover strategic insights, freelancing guides, software best practices, and AI news from the Mousa Group engineering team."}
          </p>

          {/* Search Box */}
          <div className="blog-search-box">
            <Search size={20} className="text-muted flex-shrink-0" />
            <input
              type="text"
              placeholder={isAr ? "ابحث في المقالات والموضوعات..." : "Search blog articles and topics..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Topics Chips Bar */}
          <div className="blog-topics-bar">
            {TOPICS.map((topic) => (
              <button
                key={topic.id}
                type="button"
                className={`blog-topic-chip ${selectedTopic === topic.id ? "is-active" : ""}`}
                onClick={() => setSelectedTopic(topic.id)}
              >
                {isAr ? topic.labelAr : topic.labelEn}
              </button>
            ))}
          </div>
        </section>

        {/* Articles Section Body */}
        {loading ? (
          <div className="blog-empty">
            <div className="spinner-border text-success mb-3" style={{ width: "3rem", height: "3rem" }} />
            <p className="fw-semibold text-muted">{isAr ? "جاري تحميل المقالات..." : "Loading articles..."}</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="blog-empty">
            <BookOpen size={44} className="text-muted mb-3" />
            <h2>{isAr ? "لا توجد مقالات مطابقة" : "No articles found"}</h2>
            <p className="text-muted">{isAr ? "جرّب تغيير كلمات البحث أو اختر موضوعاً آخر." : "Try adjusting your search terms or category selection."}</p>
          </div>
        ) : (
          <div className="blog-grid">
            {filteredPosts.map((post) => {
              const imageSrc = post?.image_url ? api.buildUrl(post.image_url) : "";
              return (
                <article key={post.id} className="blog-tile">
                  <Link to={`/blog/${post.slug}`} className="text-decoration-none">
                    <div className="blog-tile-image-area">
                      {imageSrc ? (
                        <img src={imageSrc} alt={post.title} />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 w-100 bg-success bg-opacity-10 text-success">
                          <BookOpen size={40} />
                        </div>
                      )}
                    </div>
                  </Link>

                  <div className="blog-tile-content">
                    <div className="blog-tile-meta">
                      <span className="d-flex align-items-center gap-1">
                        <Calendar size={14} className="text-success" />
                        {timeAgo(post.created_at, isAr)}
                      </span>
                      <span>•</span>
                      <span className="d-flex align-items-center gap-1">
                        <Clock size={14} />
                        {isAr ? "5 دقائق قراءة" : "5 min read"}
                      </span>
                    </div>

                    <h3 className="blog-tile-title">
                      <Link to={`/blog/${post.slug}`} className="text-decoration-none text-body">
                        {post.title}
                      </Link>
                    </h3>

                    {post.excerpt && (
                      <p className="blog-tile-excerpt">{post.excerpt}</p>
                    )}

                    <Link to={`/blog/${post.slug}`} className="blog-tile-link">
                      <span>{isAr ? "اقرأ المقال بالكامل" : "Read Full Article"}</span>
                      <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}